import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IoGitNetworkOutline, IoListOutline } from "react-icons/io5";
import { getLocale } from "../../../i18n/config";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import { localizedSuffix } from "../../../i18n/localizedService";
import "./PatientPlanMatrix.css";

/* -------------------------------------------------------------------------- */
/*  Помощники                                                                 */
/* -------------------------------------------------------------------------- */

// Название услуги с приоритетом языка интерфейса (same as PatientPlan)
const pickLocalizedValue = (record, key, language) => {
    const suffix = localizedSuffix(language);
    const primary = record?.[`${key}${suffix}`]?.value;
    if (primary != null && String(primary).trim() !== "") return primary;

    const secondary = record?.[suffix ? key : `${key}_en`]?.value;
    if (secondary != null && String(secondary).trim() !== "") return secondary;

    return "";
};

const appointmentNumber = (appointment) =>
    parseInt(appointment?.appointment_number?.value, 10);

const hasAdmission = (appointment) => {
    const id = appointment?.id_admission?.value;
    return id !== undefined && String(id) !== "0" && String(id) !== "";
};

const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Матричное представление плана лечения:
 * строки — направления (группы), столбцы — приёмы (этапы программы).
 * В ячейке — количество услуг приёма; клик открывает попап со списком услуг.
 */
export default function PatientPlanMatrix({
    groups = [],
    planCountAppointment,
    planId,
}) {
    const { t, i18n } = useTranslation("patient");
    const locale = getLocale(i18n.language);
    const [cell, setCell] = useState(null);

    const displayName = (record) =>
        pickLocalizedValue(record, "name", i18n.language);

    // Количество столбцов: максимум из лимита группы, лимита плана и номеров приёмов
    const stages = useMemo(() => {
        let max = 0;
        groups.forEach((group) => {
            const groupLimit = Number(group?.count_appointment?.value) || 0;
            if (groupLimit > max) max = groupLimit;
            (group?.items || []).forEach((item) => {
                (item?.appointments || []).forEach((apt) => {
                    const number = appointmentNumber(apt);
                    if (Number.isFinite(number) && number > max) max = number;
                });
            });
        });
        if (!max && Number(planCountAppointment) > 0) {
            max = Number(planCountAppointment);
        }
        return Array.from({ length: max }, (_, index) => index + 1);
    }, [groups, planCountAppointment]);

    // Услуги группы, попадающие в конкретный приём
    const getCellItems = (group, number) =>
        (group?.items || []).filter((item) =>
            (item?.appointments || []).some(
                (apt) => appointmentNumber(apt) === number,
            ),
        );

    // Текущий этап — максимальный приём, в котором уже есть назначения
    const currentStage = useMemo(() => {
        let current = 0;
        groups.forEach((group) => {
            (group?.items || []).forEach((item) => {
                (item?.appointments || []).forEach((apt) => {
                    const number = appointmentNumber(apt);
                    if (
                        hasAdmission(apt) &&
                        Number.isFinite(number) &&
                        number > current
                    ) {
                        current = number;
                    }
                });
            });
        });
        return current;
    }, [groups]);

    const getCellState = (group, number) => {
        const items = getCellItems(group, number);
        if (!items.length) {
            return currentStage === number ? "is-current" : "is-future";
        }
        const appointed = items.some((item) =>
            (item?.appointments || []).some(
                (apt) =>
                    appointmentNumber(apt) === number && hasAdmission(apt),
            ),
        );
        if (appointed) return "is-done";
        return currentStage === number ? "is-current" : "is-future";
    };

    const cellSummary = (items) => {
        if (!items.length) return t("plan.matrix.emptyCell");
        if (items.length === 1) return displayName(items[0]) || "1";
        return t("plan.matrix.servicesCount", { count: items.length });
    };

    const cellTitle = (items) =>
        items
            .map((item) => displayName(item) || "—")
            .join(", ");

    const openCell = (group, number, items) => {
        if (!items.length) return;
        setCell({ group, number, items });
    };

    const closeCell = () => setCell(null);

    const cellGroupLabel = (group) =>
        String(group?.name?.value || "").trim()
            ? getGroupTypeLabel(t, group)
            : t("plan.group.noName", { defaultValue: "—" });

    if (!groups.length) {
        return (
            <div className="page-block plan-matrix-empty">
                {t("plan.matrix.noGroups")}
            </div>
        );
    }

    return (
        <>
            <div className="card plan-matrix-card">
                <div className="card__head plan-matrix-head">
                    <div>
                        <div className="card__title plan-matrix-title">
                            <IoGitNetworkOutline size={19} aria-hidden="true" />
                            {t("plan.matrix.title")}
                        </div>
                        <div className="card__sub plan-matrix-sub">
                            {t("plan.matrix.sub")}
                        </div>
                    </div>
                </div>

                <div className="plan-matrix-wrap">
                    <div
                        className="plan-matrix"
                        style={{ "--plan-stages": stages.length || 1 }}>
                        <div className="plan-matrix__row plan-matrix__row--head">
                            <div className="plan-matrix__corner">
                                {t("plan.matrix.corner")}
                            </div>
                            {stages.map((number) => (
                                <div
                                    key={number}
                                    className={`plan-matrix__header${
                                        currentStage === number
                                            ? " is-current"
                                            : ""
                                    }`}>
                                    {t("plan.matrix.stage", { number })}
                                    {currentStage === number ? (
                                        <span>{t("plan.matrix.current")}</span>
                                    ) : null}
                                </div>
                            ))}
                        </div>

                        {groups.map((group) => (
                            <div
                                className="plan-matrix__row"
                                key={group?.id?.value}>
                                <div className="plan-matrix__rowlabel">
                                    {cellGroupLabel(group)}
                                </div>
                                {stages.map((number) => {
                                    const items = getCellItems(group, number);
                                    const state = getCellState(group, number);
                                    return (
                                        <button
                                            key={number}
                                            type="button"
                                            className={`plan-matrix__cell ${state}${
                                                items.length
                                                    ? " is-clickable"
                                                    : ""
                                            }`}
                                            title={
                                                items.length
                                                    ? cellTitle(items)
                                                    : t("plan.matrix.emptyCell")
                                            }
                                            onClick={() =>
                                                openCell(group, number, items)
                                            }
                                            disabled={!items.length}>
                                            {cellSummary(items)}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                <p className="plan-matrix-note">
                    <IoListOutline size={15} aria-hidden="true" />
                    {t("plan.matrix.note")}
                </p>
            </div>

            <Modal
                show={Boolean(cell)}
                onHide={closeCell}
                size="lg"
                centered
                className="plan-matrix-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t("plan.matrix.modalTitle", {
                            stage: cell?.number ?? "",
                        })}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {cell ? (
                        <>
                            <div className="plan-matrix-modal__sub">
                                {cellGroupLabel(cell.group)}
                                {planId
                                    ? ` · ${t("plan.matrix.planNumber", {
                                          id: planId,
                                      })}`
                                    : ""}
                            </div>
                            <ul className="plan-matrix-services">
                                {cell.items.map((item) => {
                                    const appointments = (
                                        item?.appointments || []
                                    ).filter(
                                        (apt) =>
                                            appointmentNumber(apt) ===
                                            cell.number,
                                    );
                                    const appointed = appointments.some(
                                        (apt) => hasAdmission(apt),
                                    );
                                    const comment = String(
                                        appointments[0]?.comment?.value ?? "",
                                    ).trim();

                                    return (
                                        <li
                                            className="plan-matrix-services__item"
                                            key={item?.id?.value}>
                                            <div className="plan-matrix-services__main">
                                                <div className="plan-matrix-services__name">
                                                    {displayName(item) || "—"}
                                                </div>
                                                {item?.composition?.value ? (
                                                    <div className="plan-matrix-services__comp">
                                                        {item.composition.value}
                                                    </div>
                                                ) : null}
                                                {comment ? (
                                                    <div className="plan-matrix-services__comment">
                                                        {comment}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="plan-matrix-services__side">
                                                <span className="plan-matrix-services__price">
                                                    {toNumber(
                                                        item?.amount?.value ??
                                                            item?.amount_base
                                                                ?.value,
                                                    ).toLocaleString(locale)}{" "}
                                                    ₽
                                                </span>
                                                <span
                                                    className={`plan-matrix-services__status${
                                                        appointed
                                                            ? " is-done"
                                                            : ""
                                                    }`}>
                                                    {appointed
                                                        ? t(
                                                              "plan.matrix.statusScheduled",
                                                          )
                                                        : t(
                                                              "plan.matrix.statusPlanned",
                                                          )}
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <div className="button-default light" onClick={closeCell}>
                        {t("actions.close", { ns: "common" })}
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}
